import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  NutritionEvaluationProvider,
  useNutritionEvaluationContext,
  type NutritionTab,
} from "./NutritionEvaluationContext";
import {
  NutritionProfileTab,
  DietRecallTab,
  SetTargetTab,
  MealPlanTab,
  NutritionMonitoringTab,
  NutritionFollowupTab,
} from "./tabs";

// =============================================================================
// Types
// =============================================================================

interface NutritionEvaluationHomeProps {
  appointmentId: string;
  patientId: string;
  isCompleted?: boolean;
  prescriptionUrl?: string | null;
}

// =============================================================================
// Tab Configuration
// =============================================================================

const NUTRITION_TABS: { id: NutritionTab; label: string }[] = [
  { id: "nutrition-profile", label: "Nutrition Profile" },
  { id: "diet-recall", label: "Diet Recall" },
  { id: "set-target", label: "Targets" },
  { id: "meal-plan", label: "Meal Plan" },
  { id: "monitoring", label: "Monitoring & Advice" },
  { id: "follow-up", label: "Follow-up & Referral" },
];

// =============================================================================
// Inner Content Component (uses context)
// =============================================================================

const NutritionEvaluationContent: React.FC<{
  prescriptionUrl?: string | null;
}> = ({ prescriptionUrl }) => {
  const { activeTab, setActiveTab, appointmentId, isCompleted } =
    useNutritionEvaluationContext();

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as NutritionTab)}
      className="flex flex-col h-full gap-0"
    >
      {/* Tab Navigation */}
      <div className="bg-card border-b border-border/60 px-4 py-3">
        <TabsList>
          {NUTRITION_TABS.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="z-secondary data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-border/60"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto scrollbar-stable bg-background p-6">
        <TabsContent value="nutrition-profile">
          <NutritionProfileTab />
        </TabsContent>

        <TabsContent value="diet-recall">
          <DietRecallTab />
        </TabsContent>

        <TabsContent value="set-target">
          <SetTargetTab />
        </TabsContent>

        <TabsContent value="meal-plan">
          <MealPlanTab />
        </TabsContent>

        <TabsContent value="monitoring">
          <NutritionMonitoringTab
            appointmentId={appointmentId}
            isCompleted={isCompleted}
          />
        </TabsContent>

        <TabsContent value="follow-up">
          <NutritionFollowupTab
            appointmentId={appointmentId}
            isCompleted={isCompleted}
            prescriptionUrl={prescriptionUrl}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
};

// =============================================================================
// Main Component with Provider
// =============================================================================

export const NutritionEvaluationHome: React.FC<
  NutritionEvaluationHomeProps
> = ({ appointmentId, patientId, isCompleted = false, prescriptionUrl }) => {
  return (
    <NutritionEvaluationProvider
      appointmentId={appointmentId}
      patientId={patientId}
      isCompleted={isCompleted}
    >
      <NutritionEvaluationContent prescriptionUrl={prescriptionUrl} />
    </NutritionEvaluationProvider>
  );
};
